import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { get } from '~/lib/api'
import type { Stage } from '~/lib/types'
import Spinner from '~/components/Spinner'
import StageBadge from '~/components/StageBadge'
import { Button } from '~/components/ui/button'
import { Card, CardContent } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'

type Student = {
  id: number
  name: string
  phone: string
  email: string | null
  course: string | null
  stage: Stage | null
  conversation_id: number | null
  registered_at: string | null
  created_at: string
}

type StudentsPayload = {
  students: Student[]
  total: number
  page: number
  per_page: number
}

export default function Students() {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['students', query, page],
    queryFn: () =>
      get<StudentsPayload>(`/api/v1/students?page=${page}&q=${encodeURIComponent(query)}`),
    // Keeps the table on screen while the next page loads instead of flashing.
    placeholderData: keepPreviousData,
  })

  const lastPage = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1
  const empty = !isPending && data?.students.length === 0

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Students</h1>
          <p className="mt-1 text-sm text-ink-2">
            Everyone who has enquired, and where the agent has got to with them.
          </p>
        </div>

        {/* Both land in the admin panel, which Rails renders — plain anchors,
            not router links. */}
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <a href="/admin/imports/new">Import a sheet</a>
          </Button>
          <Button asChild size="sm">
            <a href="/admin/students/new">Add a student</a>
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          type="search"
          value={query}
          placeholder="Search name, phone or email"
          className="max-w-xs"
          onChange={(event) => {
            setQuery(event.target.value)
            setPage(1)
          }}
        />
        {isFetching && !isPending && <Spinner label="Updating" />}
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {isPending ? (
            <div className="p-8">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-5">Student</TableHead>
                  <TableHead className="px-5">Phone</TableHead>
                  <TableHead className="px-5">Course</TableHead>
                  <TableHead className="px-5">Stage</TableHead>
                  <TableHead className="px-5">Added</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {data?.students.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell className="px-5 py-3">
                      <p className="font-medium">{student.name}</p>
                      {student.email && <p className="text-xs text-faint">{student.email}</p>}
                    </TableCell>
                    <TableCell className="px-5 py-3 [font-variant-numeric:tabular-nums]">
                      {student.phone}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      {student.course ?? <span className="text-faint">—</span>}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      {/* No conversation yet is a real state, and the one the
                          "start a conversation" work will act on. */}
                      {student.stage ? (
                        <StageBadge stage={student.stage} />
                      ) : (
                        <span className="text-faint">Not contacted</span>
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3 [font-variant-numeric:tabular-nums]">
                      {student.conversation_id ? (
                        <Link
                          to="/conversations/$conversationId"
                          params={{ conversationId: String(student.conversation_id) }}
                          // The thread route validates its search params, so
                          // arriving from here has to supply the defaults the
                          // conversations list would have carried.
                          search={{ stage: 'all', escalated: false }}
                          className="underline underline-offset-2 hover:text-ink"
                        >
                          View thread
                        </Link>
                      ) : (
                        new Date(student.created_at).toLocaleDateString()
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {empty && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-faint">
                      {query ? (
                        <>No students match “{query}”.</>
                      ) : (
                        <>
                          No students yet.{' '}
                          <a href="/admin/imports/new" className="underline underline-offset-2">
                            Import a sheet
                          </a>{' '}
                          or{' '}
                          <a href="/admin/students/new" className="underline underline-offset-2">
                            add one
                          </a>
                          .
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <p className="text-faint">
          {data?.total ?? 0} student{data?.total === 1 ? '' : 's'}
        </p>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="text-faint">
            Page {page} of {lastPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
